import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
    GlobalAccount,
    GUID,
    NoteView,
    TaskCommentRequest,
    TaskRequest,
    TaskStatus,
    TaskTimelineEntryView,
    TaskUpdateStrategy,
    TaskView,
} from '@ndvr/customer-api';

import { MentionSuggestionItem } from '@/components/minimal-tiptap/extensions';
import { CustomerApi } from 'API';
import { formatDate } from 'utils';
import { allTenantTeamMembers, useTenantTeamQuery } from 'views/admin/tenantTeam/tenant-team-queries';
import { useSelectedTenantGUIDs } from 'views/auth/tenant-selection';
import { useGlobalAccount } from 'views/household/household-queries';

import { ActionItemQueryKeys } from '../household/HouseholdNotes/action-items-query';
import { useTaskViewState } from './task-state';
import { getDueDate } from './task-utils';

const TaskQueryKeys = {
    listTasksByHouseholdQuery: (householdGUID: GUID<GlobalAccount>, eventGUID?: GUID<TaskView>) => [
        'tasks',
        householdGUID,
        eventGUID,
    ],
    ALL_TASK_VIEWS: ['tasks'],
    dueTaskViews: (tenantGUIDs: string[], dueDate: Date) => ['tasks', 'tenants', tenantGUIDs, dueDate.toISOString()],
    taskView: (taskGuid: GUID<TaskView>) => ['tasks', taskGuid],
    taskTimeline: (taskGuid: GUID<TaskView>) => ['tasks', taskGuid, 'timeline'],
    tasksByNote: (noteGUID: GUID<NoteView>) => ['tasks', 'note', noteGUID],
} as const;

type TaskData = Omit<TaskRequest, 'title' | 'description'> & {
    title: string;
    description: string;
    householdGuid: string;
};

type TenantTaskData = Omit<TaskData, 'householdGuid'> & { tenantGuid: string };

export const useCreateTask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: TaskData) => {
            const [taskViewGuid] = await CustomerApi().createTask(data.householdGuid, {
                ...data,
                title: data.title,
                description: data.description,
            });
            return taskViewGuid;
        },
        onSuccess: (_taskGuid, variables) => {
            if (variables?.noteGUID) {
                queryClient.invalidateQueries({
                    queryKey: ActionItemQueryKeys.actionItemsByNote(variables.noteGUID),
                });
                queryClient.invalidateQueries({
                    queryKey: TaskQueryKeys.tasksByNote(variables.noteGUID),
                });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.ALL_TASK_VIEWS,
            });
        },
    });
};

export const useCreateTaskForTenant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: TenantTaskData) => {
            const [taskViewGuid] = await CustomerApi().createTaskForTenant(data.tenantGuid, {
                ...data,
                title: data.title,
                description: data.description,
            });
            return taskViewGuid;
        },
        onSuccess: (_taskGuid, variables) => {
            if (variables?.noteGUID) {
                queryClient.invalidateQueries({
                    queryKey: ActionItemQueryKeys.actionItemsByNote(variables.noteGUID),
                });
                queryClient.invalidateQueries({
                    queryKey: TaskQueryKeys.tasksByNote(variables.noteGUID),
                });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.ALL_TASK_VIEWS,
            });
        },
    });
};

export const useListTasksByHouseholdQuery = (householdGUID: string, eventGUID?: string) =>
    useQuery<TaskView[]>({
        enabled: !!householdGUID,
        queryKey: TaskQueryKeys.listTasksByHouseholdQuery(householdGUID, eventGUID),
        queryFn: async () => {
            const [response] = await CustomerApi().listTasksByHousehold(householdGUID, { eventGUID });
            return response || [];
        },
    });

export const useListTasksByNoteQuery = (noteGUID: string) =>
    useQuery<TaskView[]>({
        enabled: !!noteGUID,
        queryKey: TaskQueryKeys.tasksByNote(noteGUID),
        queryFn: async () => {
            const [tasks] = await CustomerApi().listByNote(noteGUID);
            return tasks ?? [];
        },
    });

// Field edit only — scope (household/tenant) is never changed here; attaching to a household is a
// dedicated action below. The request body is built explicitly, so any scope fields a caller
// happens to carry are not sent.
export type UpdateTaskInput = Omit<TaskData, 'householdGuid'> & {
    taskGUID: GUID<TaskView>;
};

export const useUpdateTask = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: UpdateTaskInput) => {
            await CustomerApi().updateTask(data.taskGUID, {
                assignee: data.assignee,
                title: data.title,
                description: data.description,
                dueDate: data.dueDate,
                type: data.type,
                status: data.status,
                noteGUID: data.noteGUID,
                eventGUID: data.eventGUID,
                actionItemGUID: data.actionItemGUID,
                frequency: data.frequency,
                taskUpdateStrategy: data.taskUpdateStrategy,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.ALL_TASK_VIEWS,
            });
        },
    });
};

// Attach a tenant-only task (and its recurring series) to a household. Household is sticky once set.
export const useAttachTaskToHousehold = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { taskGUID: GUID<TaskView>; householdGUID: string }) => {
            await CustomerApi().attachTaskToHousehold(data.taskGUID, data.householdGUID);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.ALL_TASK_VIEWS,
            });
        },
    });
};

export const useDueTaskViews = (dueDate: Date) => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: TaskQueryKeys.dueTaskViews(tenantGUIDs, dueDate),
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const cutoffDate = formatDate(dueDate, 'SERVER');

            const [tasks] = await CustomerApi().listTasksForTenants(tenantGUIDs, {
                cutoffDate,
            });
            return tasks ?? [];
        },
    });
};

export const useTasksForPriorityWindow = (cutoffDate: Date) => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: TaskQueryKeys.dueTaskViews(tenantGUIDs, cutoffDate),
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const formattedCutoffDate = formatDate(cutoffDate, 'SERVER');

            const [tasks] = await CustomerApi().listTasksForTenants(tenantGUIDs, {
                cutoffDate: formattedCutoffDate,
            });
            return tasks ?? [];
        },
    });
};

export const useTaskView = (taskGuid: GUID<TaskView>) => {
    return useQuery({
        queryKey: TaskQueryKeys.taskView(taskGuid),
        queryFn: async () => {
            const [task] = await CustomerApi().getTask(taskGuid);
            return task ?? null;
        },
    });
};

export const useTaskTimeline = (taskGuid: GUID<TaskView>) =>
    useQuery<TaskTimelineEntryView[]>({
        enabled: !!taskGuid,
        queryKey: TaskQueryKeys.taskTimeline(taskGuid),
        queryFn: async () => {
            const [timeline] = await CustomerApi().listTaskTimeline(taskGuid);
            return timeline ?? [];
        },
    });

export const useCreateTaskComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ taskGuid, request }: { taskGuid: GUID<TaskView>; request: TaskCommentRequest }) => {
            await CustomerApi().createTaskComment(taskGuid, request);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.taskTimeline(variables.taskGuid),
            });
        },
    });
};

export const useUpdateTaskComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({
            taskGuid,
            commentGUID,
            request,
        }: {
            taskGuid: GUID<TaskView>;
            commentGUID: string;
            request: TaskCommentRequest;
        }) => {
            await CustomerApi().updateTaskComment(taskGuid, commentGUID, request);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.taskTimeline(variables.taskGuid),
            });
        },
    });
};

export const useDeleteTaskComment = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ taskGuid, commentGUID }: { taskGuid: GUID<TaskView>; commentGUID: string }) => {
            await CustomerApi().deleteTaskComment(taskGuid, commentGUID);
        },
        onSettled: (_data, _error, variables) => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.taskTimeline(variables.taskGuid),
            });
        },
    });
};

export const useDeleteTask = () => {
    const queryClient = useQueryClient();
    type DeleteArgs = { taskViewGuid: GUID<TaskView>; taskUpdateStrategy?: TaskUpdateStrategy };
    return useMutation({
        mutationFn: async ({ taskViewGuid, taskUpdateStrategy }: DeleteArgs) => {
            await CustomerApi().delete(taskViewGuid, { taskUpdateStrategy });
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.ALL_TASK_VIEWS,
            });
        },
    });
};

export const useUpdateTaskStatus = () => {
    const queryClient = useQueryClient();
    const { taskViewState } = useTaskViewState();
    const tenantGUIDs = useSelectedTenantGUIDs();

    const mutateTask = (task: TaskView) => {
        queryClient.setQueryData(
            TaskQueryKeys.dueTaskViews(tenantGUIDs, getDueDate(taskViewState)),
            (data: TaskView[] | undefined) =>
                data?.map((item) =>
                    item.taskGUID === task.taskGUID ? { ...task, updatedAt: new Date().toISOString() } : item,
                ) ?? data,
        );
    };

    return useMutation({
        mutationFn: async (data: { task: TaskView; status: TaskStatus }) => {
            // opportunistic update
            mutateTask({
                ...data.task,
                status: data.status,
            });
            await CustomerApi().updateTaskStatus(data.task.taskGUID, {
                status: data.status,
            });
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: TaskQueryKeys.ALL_TASK_VIEWS,
            });
        },
    });
};

export const useOutstandingTasks = () => {
    const tenantGUIDs = useSelectedTenantGUIDs();

    return useQuery({
        queryKey: ['tasks', 'outstanding', 'tenants', tenantGUIDs],
        enabled: tenantGUIDs.length > 0,
        queryFn: async () => {
            const [tasks] = await CustomerApi().listOverdueIncompleteTasksByTenants(tenantGUIDs);
            return tasks ?? [];
        },
    });
};

export const useTaskMentionSuggestions = (
    globalAccountGUID: GUID<GlobalAccount> | undefined,
): MentionSuggestionItem[] => {
    const { data: globalAccount } = useGlobalAccount(globalAccountGUID);
    const { data: team } = useTenantTeamQuery(globalAccount?.tenantGUID);
    return useMemo(
        () =>
            allTenantTeamMembers(team).map((member) => ({
                id: member.internalUserGUID,
                label: member.name,
            })),
        [team],
    );
};
